import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { buildContext } from "../lib/build-context";
import { cn } from "../lib/utils";

export const buttonVariants = cva(
  "font-inter leading-[1.2] inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-semibold transition-colors focus-visible:outline-none disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 align-middle self-center",
  {
    variants: {
      variant: {
        ghost: "bg-transparent hover:bg-transparent disabled:bg-transparent disabled:hover:bg-transparent",
        black:
          "bg-textSecondary hover:bg-textPrimary text-white disabled:bg-borderLight disabled:hover:bg-borderLight disabled:text-borderDark",
        outlined:
          "bg-white hover:bg-backgroundLight text-textPrimary border border-borderLight disabled:bg-white disabled:hover:bg-white disabled:text-borderDark",
        secondary:
          "bg-borderLight hover:bg-borderDark text-textPrimary border border-borderLight hover:border-borderDark disabled:bg-borderLight disabled:hover:bg-borderLight disabled:text-borderDark",
        error:
          "bg-colorRed text-white border border-colorRed hover:bg-colorRedDark hover:border-colorRedDark disabled:bg-colorRedLight disabled:hover:bg-colorRedLight disabled:border-colorRedLight",
      },
      size: {
        sm: "px-4 py-[7px] text-xs h-[30px]",
        md: "px-4 py-[10px] text-xs h-[36px]",
        lg: "px-6 py-[14px] text-sm h-[46px]",
      },
    },
    defaultVariants: {
      variant: "black",
      size: "md",
    },
  },
);

const [ButtonProvider, useButtonContext] = buildContext<VariantProps<typeof buttonVariants>>("Button");

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean | string;
  withArrow?: boolean;
  leftAddon?: React.ReactNode;
  rightAddon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "black",
      size = "md",
      loading = false,
      withArrow = false,
      leftAddon,
      rightAddon,
      children,
      disabled,
      onClick,
      ...props
    },
    ref,
  ) => {
    const isLoading = typeof loading === "string" || loading === true;
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (loading) return;
      onClick?.(e);
    };

    return (
      <ButtonProvider size={size} variant={variant}>
        <button
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          onClick={handleClick}
          disabled={disabled || isLoading}
          {...props}
        >
          {leftAddon}
          {isLoading ? (typeof loading === "string" ? loading : `${children}...`) : children}
          {withArrow ? <ButtonArrowRightIcon /> : rightAddon}
        </button>
      </ButtonProvider>
    );
  },
);
Button.displayName = "Button";

export function ButtonPlusIcon() {
  const { size } = useButtonContext();
  const iconSize = {
    sm: "size-3",
    md: "size-3",
    lg: "size-4",
  }[size!];
  return <i className={`inline-block i-bp-plus ${iconSize}`} />;
}

export function ButtonArrowRightIcon() {
  const { size } = useButtonContext();
  const iconSize = {
    sm: "size-3",
    md: "size-3",
    lg: "size-4",
  }[size!];
  return <i className={`inline-block i-bp-arrow-right ${iconSize}`} />;
}

export function ButtonDropdownIcon() {
  const { size } = useButtonContext();
  const iconSize = {
    sm: "size-3",
    md: "size-4",
    lg: "size-4",
  }[size!];
  return <i className={`inline-block i-bp-chevron-down ${iconSize}`} />;
}
